import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getCurrencyTimeseries } from '@/lib/api';

export default function CurrencyMiniChart({ from, to, days = 30 }) {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!from || !to || from === to) { setPoints(null); setLoading(false); return; }
    let alive = true; setLoading(true);
    getCurrencyTimeseries(from, to, days)
      .then((d) => { if (alive) setPoints(d.points || []); })
      .catch(() => { if (alive) setPoints([]); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [from, to, days]);

  const { path, areaPath, change, changePct, w, h } = useMemo(() => {
    if (!points || points.length < 2) return { path: '', areaPath: '', change: 0, changePct: 0, w: 220, h: 44 };
    const w = 220; const h = 44;
    const rates = points.map((p) => p.rate);
    const min = Math.min(...rates); const max = Math.max(...rates);
    const span = Math.max(0.0001, max - min);
    const dx = w / (points.length - 1);
    const ys = rates.map((r) => h - ((r - min) / span) * (h - 4) - 2);
    const segments = ys.map((y, i) => `${i === 0 ? 'M' : 'L'}${(i * dx).toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${segments} L${w},${h} L0,${h} Z`;
    const first = rates[0]; const last = rates[rates.length - 1];
    const change = last - first; const changePct = (change / first) * 100;
    return { path: segments, areaPath: area, change, changePct, w, h };
  }, [points]);

  if (from === to) return null;
  if (loading) return <div className="mt-3 puvio-skeleton h-11 w-full" data-testid="currency-sparkline-skeleton" />;
  if (!points || points.length < 2) return null;
  const up = change > 0; const flat = Math.abs(changePct) < 0.05;
  const color = flat ? '#7A8A80' : up ? '#2ED47A' : '#FF4D4F';
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;

  return (
    <div className="mt-3" data-testid="currency-sparkline">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-[var(--puvio-text-3)]">Last {days} days</span>
        <span className="inline-flex items-center gap-1 font-medium" style={{ color }}>
          <Icon className="w-3 h-3" /> {change >= 0 ? '+' : ''}{changePct.toFixed(2)}%
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-11 block">
        <defs>
          <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.30" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sparkfill)" />
        <path d={path} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </div>
  );
}
